import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firestore';
import { loadGamesCached, loadPublicMerchandise, loadPublicPressReleases } from '../services/adminData';
import { getSponsorsForSurface } from '../services/publicSponsors';
import { GetTicketsButton, GameTicketStatus } from '../utils/gameTickets';
import { cachedRead, cacheKeys, CACHE_TTL } from '../services/adminCache';
import { toJsDate } from '../utils/safeDate';
import type { MerchandiseItem, Game, Ticket, PressRelease, Notification, Sponsor } from '../types/global';
import { normalizeTier, tierRank, tierLabel, TIER_ORDER } from '../config/sponsorTiers';
import { CURRENT_SEASON_SHORT } from '../globalVariables';
import '../styles/SplashPage.css';
import FeaturedItem from '../components/FeaturedItem';
import NotificationBanner from '../components/NotificationBanner';
import PartnerSlot from '../components/PartnerSlot';
import SponsorCarousel from '../components/SponsorCarousel';
import vipersMain from '../assets/WhiteVipers.png';
import heroLockerRoom from '../assets/hero-locker-room.jpg';
import {
  ChevronRight,
  Calendar,
  ShoppingBag,
  Clock,
  MapPin,
  PlayCircle,
  X,
  ArrowRight,
  ArrowUp,
  Ticket as TicketIcon,
  Check,
  ExternalLink,
  Instagram,
  Megaphone,
} from 'lucide-react';

// Lazy load components to improve initial load time
const Footer = lazy(() => import('../components/Footer'));


const YOUTUBE_LIVE_STREAM = 'https://youtube.com/live'; // Replace with actual URL if needed.

// BRUCE: This is where we set up Firebase configuration
// YOU NEED TO:
// 1. Create a Firebase project in the Firebase Console if not already done
// 2. Get these values from the Firebase Console under Project Settings
// 3. Add them to the .env file in the root of our project like this:
//    REACT_APP_FIREBASE_API_KEY=your-api-key
//    REACT_APP_FIREBASE_AUTH_DOMAIN=your-auth-domain
//    REACT_APP_FIREBASE_PROJECT_ID=your-project-id
//    REACT_APP_FIREBASE_STORAGE_BUCKET=your-storage-bucket
//    REACT_APP_FIREBASE_MESSAGING_SENDER_ID=your-sender-id
//    REACT_APP_FIREBASE_APP_ID=your-app-id
//    REACT_APP_FIREBASE_MEASUREMENT_ID=your-measurement-id
// 4. Make sure the .env file is in .gitignore so we don't commit sensitive keys

// BRUCE: Initialize Firebase with the config above
// This connects our frontend to the Firebase backend

// Configuration & Constants
// (Logos that fail to load now fall back to a monogram — see LogoImg.)

const socialLinks = {
  facebook: 'https://facebook.com/vipersmhl',
  twitter: 'https://twitter.com/vipersmhl',
  instagram: 'https://instagram.com/vipersmhl',
  youtube: 'https://youtube.com/live/2M54ubCmSBY?feature=share',
};

type FeaturedItem = 
  | (MerchandiseItem & { type: 'merchandise' })
  | (Ticket & { type: 'ticket' });

interface ShowDetailsState {
  type: 'merchandise' | 'ticket' | null;
  item: (MerchandiseItem & { type: 'merchandise' }) | (Ticket & { type: 'ticket' }) | null;
}

// ── Presentation helpers ────────────────────────────────────────────────────

// Two-letter monogram used when a logo is missing or fails to load.
const monogram = (name: string): string =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

// Image that swaps to a fallback node (monogram / name) instead of a broken
// image or a grey "150x150" placeholder.
const LogoImg: React.FC<{
  src?: string;
  alt: string;
  className?: string;
  fallback: React.ReactNode;
  fallbackClassName?: string;
  eager?: boolean;
}> = ({ src, alt, className = '', fallback, fallbackClassName = '', eager = false }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className={`${className} ${fallbackClassName}`} role="img" aria-label={alt}>
        {fallback}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

// SplashPage Component
const SplashPage: React.FC = () => {
  usePageMeta('Breck Vipers | Official Mountain Hockey League Team | Breckenridge, Colorado', 'Official site of the Breck Vipers, Breckenridge Colorado\'s official Mountain Hockey League team. Buy tickets, shop merchandise, view schedule, and meet the team.');
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [, setActiveGameIndex] = useState(0); // legacy carousel tick (list no longer rotates)
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showNewsPanel, setShowNewsPanel] = useState(false);
  const [activeNewsIndex, setActiveNewsIndex] = useState(0);
  const [showTicketAlert, setShowTicketAlert] = useState(false);
  const [showGameAlert, setShowGameAlert] = useState(false);
  const [showMerchAlert, setShowMerchAlert] = useState(false);
  const [showSeasonPopup, setShowSeasonPopup] = useState(false);
  const [showMerchPopup, setShowMerchPopup] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [upcomingGames, setUpcomingGames] = useState<Game[]>([]);
  const [featuredMerchandise, setFeaturedMerchandise] = useState<(MerchandiseItem & { type: 'merchandise' })[]>([]);
  const [featuredTickets, setFeaturedTickets] = useState<(Ticket & { type: 'ticket' })[]>([]);
  const [latestNews, setLatestNews] = useState<PressRelease[]>([]);
  const [featuredSponsors, setFeaturedSponsors] = useState<Sponsor[]>([]);
  const [nextHomeGame, setNextHomeGame] = useState<Game | null>(null);
  const [firebaseNotifications, setFirebaseNotifications] = useState<Notification[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<string[]>([]);
  const [isLoadingGames, setIsLoadingGames] = useState(true);
  const [isLoadingMerch, setIsLoadingMerch] = useState(true);
  const [isLoadingTickets, setIsLoadingTickets] = useState(true);
  const [isLoadingNews, setIsLoadingNews] = useState(true);
  const [errorGames, setErrorGames] = useState<string | null>(null);
  const [errorMerch, setErrorMerch] = useState<string | null>(null);
  const [errorTickets, setErrorTickets] = useState<string | null>(null);
  const [errorNews, setErrorNews] = useState<string | null>(null);
  const [activeCountdownGame, setActiveCountdownGame] = useState<Game | null>(
    null
  );

  const videoRef = useRef<HTMLIFrameElement>(null);
  const newsPanelRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  // Add state for showing details
  const [showDetails, setShowDetails] = useState<ShowDetailsState>({ type: null, item: null });

  // Helper: Show Notification (unchanged)
  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info'
  ) => {
    console.log(`Notification (${type}): ${message}`);
  };

  // Helper: Dismiss Firebase Notification
  const dismissNotification = (notificationId: string) => {
    setDismissedNotifications(prev => [...prev, notificationId]);
    
    // Also hide the corresponding alert
    const notification = firebaseNotifications.find(n => n.id === notificationId);
    if (notification) {
      if (notification.type === 'announcement' && notification.priority === 'high') {
        setShowGameAlert(false);
      } else if (notification.type === 'promotion') {
        setShowMerchAlert(false);
      }
    }
  };

  // Helper: Get Full Product Image URL
  const getProductImageUrl = (
    imageUrl: string,
    productName: string
  ): string => {
    // Store FULL image URLs in Firestore (e.g., https://your-bucket/image.jpg).
    // If the imageUrl is empty, we fall back to a placeholder
    if (!imageUrl || imageUrl.trim() === '') {
      return `https://placehold.co/400x500/cccccc/333333?text=${encodeURIComponent(
        productName
      )}`;
    }
    // Return the image URL as-is since Firestore should have complete URLs
    return imageUrl;
  };

  // Helper: Convert 12-Hour Time to 24-Hour Format (unchanged)
  const convertTo24Hour = (time12h: string): string => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = (parseInt(hours, 10) + 12).toString();
    return `${hours}:${minutes}:00`;
  };

  // Parallelized data fetching helpers
  const fetchMerchandise = async (): Promise<(MerchandiseItem & { type: 'merchandise' })[]> => {
    // Whole catalog, cached once per session and shared with the shop page.
    // Prefer featured+sale, fall back to featured only, cap client-side.
    const all = await loadPublicMerchandise<Record<string, any>>();
    // Admin "Feature on Homepage" toggle; hidden (inactive) products never show.
    const featured = all.filter((m) => (m as any).featured && (m as any).active !== false);
    const featuredSale = featured.filter((m) => (m as any).sale);
    const chosen = featuredSale.length > 0 ? featuredSale.slice(0, 6) : featured.slice(0, 8);

    const items = chosen.map(raw => {
      const { id, ...data } = raw as { id: string } & Record<string, any>;
      return {
        ...data,
        id,
        type: 'merchandise' as const,
        image: data.image || '',
        price: data.price || 0,
        name: data.name || 'Unnamed Item',
        sizes: data.sizes || [],
        colors: data.colors || [],
        featured: data.featured || false,
        sale: data.sale || false
      };
    }) as (MerchandiseItem & { type: 'merchandise' })[];

    return items;
  };

  // Admin-governed: active, one per brand, homepageCarousel tier/policy gates,
  // tier → Display Order → name. Platinum-only filtering stays in homeSponsors.
  const fetchSponsors = (): Promise<Sponsor[]> => getSponsorsForSurface('homepageCarousel');

  const fetchTickets = async (): Promise<(Ticket & { type: 'ticket' })[]> => (
    // Cached once per session: this read + its per-ticket gameRef lookups are the
    // priciest homepage fetch, and featured tickets change rarely.
    cachedRead(cacheKeys.custom('splashTickets'), async () => {
    console.log('Fetching featured tickets...');
    let ticketsSnapshot;
    let ticketsQuery;

    try {
      ticketsQuery = query(
        collection(db, 'tickets'),
        where('featured', '==', true),
        where('isOnSale', '==', true),
        orderBy('eventDate', 'asc')
      );
      ticketsSnapshot = await getDocs(ticketsQuery);
      console.log('Featured + on sale tickets found:', ticketsSnapshot.size);
    } catch (indexError) {
      console.log('Featured+isOnSale index not available, trying featured only...');
      ticketsQuery = query(
        collection(db, 'tickets'),
        where('featured', '==', true),
        limit(8)
      );
      ticketsSnapshot = await getDocs(ticketsQuery);
      console.log('Featured tickets (no ordering) found:', ticketsSnapshot.size);
    }

    if (ticketsSnapshot.empty) {
      console.log('No featured tickets found, getting upcoming tickets...');
      const now = new Date();
      ticketsQuery = query(
        collection(db, 'tickets'),
        where('eventDate', '>=', now),
        limit(6)
      );
      ticketsSnapshot = await getDocs(ticketsQuery);
      console.log('Upcoming tickets found:', ticketsSnapshot.size);
    }

    // Resolve all gameRef lookups in parallel
    const items = await Promise.all(ticketsSnapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      let eventTime: string | undefined;
      let eventDate = data.eventDate;
      let eventLocation = data.eventLocation || 'TBD';

      if (data.gameRef) {
        try {
          const gameDoc = await getDoc(data.gameRef);
          if (gameDoc.exists()) {
            const gameData = gameDoc.data() as Game;
            eventTime = gameData?.eventTime;
            eventDate = gameData?.eventDate || eventDate;
            eventLocation = gameData?.eventLocation || eventLocation;
          }
        } catch (err) {
          console.warn('Failed to fetch game data for ticket', docSnap.id, err);
        }
      }

      return {
        ...data,
        id: docSnap.id,
        type: 'ticket' as const,
        name: data.name || 'Unnamed Event',
        price: data.price || 0,
        eventDate,
        eventTime,
        eventLocation,
        images: data.images || [],
        featured: data.featured || false,
        isOnSale: data.isOnSale || false
      };
    })) as (Ticket & { type: 'ticket' })[];

    console.log('Final featured tickets:', items.length);
    return items;
    }, CACHE_TTL.REFERENCE)
  );

  const fetchNews = async (): Promise<PressRelease[]> => {
    // Whole pressReleases collection, cached once per session and shared with the
    // detail pages. Filter published + sort desc + take 3 client-side.
    const rows = await loadPublicPressReleases();
    const toMs = (d: any): number => {
      try { return d?.toDate ? d.toDate().getTime() : (d ? new Date(d).getTime() : 0); } catch { return 0; }
    };
    // Normalize the fields the render reads directly. The old query used
    // orderBy('publishDate'), which silently EXCLUDED docs missing that field;
    // the client-side path doesn't, so a published doc with a missing/string
    // publishDate (or no images/category/tags) would otherwise crash the homepage.
    const items = rows
      .map(row => {
        const data = row.data as any;
        const pd = data.publishDate;
        return {
          ...data,
          id: row.id,
          slug: data.slug || data.title?.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').substring(0, 50) || row.id,
          publishDate: pd?.toDate ? pd : (pd ? Timestamp.fromDate(new Date(pd)) : Timestamp.now()),
          category: data.category || 'News',
          images: Array.isArray(data.images) ? data.images : [],
          tags: Array.isArray(data.tags) ? data.tags : []
        };
      })
      .filter((it: any) => it.published === true)
      .sort((a: any, b: any) => toMs(b.publishDate) - toMs(a.publishDate))
      .slice(0, 3) as PressRelease[];

    console.log('Latest news items:', items.length);
    return items;
  };

  const fetchGames = async (): Promise<(Game & { type: 'game' })[]> => {
    // Read all games from the shared 20-min cache (reused across the whole
    // customer session — homepage, schedule, tickets), then filter to upcoming
    // and sort client-side. Games is a small collection; caching it saves a
    // Firestore read on every page view.
    const nowMs = Date.now();
    const ms = (g: Game): number => {
      const d = (g as unknown as { eventDate?: { toMillis?: () => number } | string | number }).eventDate;
      if (d && typeof (d as { toMillis?: () => number }).toMillis === 'function') return (d as { toMillis: () => number }).toMillis();
      const t = d ? new Date(d as string | number).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };
    const all = (await loadGamesCached<Game>()) as Game[];
    return all
      .filter((g) => ms(g) >= nowMs)
      .sort((a, b) => ms(a) - ms(b))
      // Normalize eventDate to a real Timestamp so downstream render `.toDate()`
      // can never crash on a doc that stored the date as a string/number.
      .map((g) => ({ ...g, eventDate: Timestamp.fromDate(new Date(ms(g))), type: 'game' as const }));
  };

  // Fetch all data in parallel
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingGames(true);
      setIsLoadingMerch(true);
      setIsLoadingTickets(true);
      setIsLoadingNews(true);
      setErrorGames(null);
      setErrorMerch(null);
      setErrorTickets(null);
      setErrorNews(null);

      const [merchResult, sponsorsResult, ticketsResult, newsResult, gamesResult] = await Promise.allSettled([
        fetchMerchandise(),
        fetchSponsors(),
        fetchTickets(),
        fetchNews(),
        fetchGames()
      ]);

      // Process merchandise
      if (merchResult.status === 'fulfilled') {
        setFeaturedMerchandise(merchResult.value);
      } else {
        console.error('Error fetching featured merchandise:', merchResult.reason);
        const msg = merchResult.reason instanceof Error && merchResult.reason.message.includes('index')
          ? 'Merchandise index is being built. Please try again in a few minutes.'
          : 'Failed to load featured merchandise';
        setErrorMerch(msg);
      }
      setIsLoadingMerch(false);

      // Process sponsors
      if (sponsorsResult.status === 'fulfilled') {
        setFeaturedSponsors(sponsorsResult.value);
      } else {
        console.error('Error fetching sponsors:', sponsorsResult.reason);
      }

      // Process tickets
      if (ticketsResult.status === 'fulfilled') {
        setFeaturedTickets(ticketsResult.value);
      } else {
        console.error('Error fetching featured tickets:', ticketsResult.reason);
        const msg = ticketsResult.reason instanceof Error && ticketsResult.reason.message.includes('index')
          ? 'Tickets index is being built. Please try again in a few minutes.'
          : 'Failed to load featured tickets';
        setErrorTickets(msg);
      }
      setIsLoadingTickets(false);

      // Process news
      if (newsResult.status === 'fulfilled') {
        setLatestNews(newsResult.value);
      } else {
        console.error('Error fetching press releases:', newsResult.reason);
        const msg = newsResult.reason instanceof Error && newsResult.reason.message.includes('index')
          ? 'News index is being built. Please try again in a few minutes.'
          : 'Failed to load latest news';
        setErrorNews(msg);
      }
      setIsLoadingNews(false);

      // Process games
      if (gamesResult.status === 'fulfilled') {
        const games = gamesResult.value;
        setUpcomingGames(games);
        setNextHomeGame(games[0] || null);
        const homeGames = games.filter(game => game.isHome);
        if (homeGames.length > 0) {
          // Admin "Feature on homepage (Puck Drop)" wins; otherwise the next home game.
          setActiveCountdownGame(homeGames.find(game => game.featured) || homeGames[0]);
        }
      } else {
        console.error('Error fetching games:', gamesResult.reason);
        setErrorGames('Failed to load games');
      }
      setIsLoadingGames(false);
    };

    fetchData();
    const timer = setTimeout(() => setIsLoaded(true), 100);
    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Countdown Timer for Next Game (unchanged)
  useEffect(() => {
    if (!activeCountdownGame) return;

    const getCountdownDate = (): Date | null => {
      // eventDate may be a Timestamp or a cached string; toJsDate handles both.
      const eventDate = toJsDate(activeCountdownGame.eventDate as never);
      if (!eventDate) return null;
      const timeStr = activeCountdownGame.eventTime;
      // With a valid time, combine date + time; otherwise fall back to the date
      // (midnight) so the countdown is still a real number rather than NaN.
      const combined = timeStr
        ? new Date(`${eventDate.toISOString().split('T')[0]}T${timeStr}`)
        : eventDate;
      return Number.isNaN(combined.getTime()) ? eventDate : combined;
    };

    const nextGameDate = getCountdownDate();
    if (!nextGameDate) return; // no valid date → skip the countdown timer

    const timer = setInterval(() => {
      const now = new Date();
      const difference = nextGameDate.getTime() - now.getTime();

      if (difference <= 0) {
        clearInterval(timer);
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        const now = new Date();
        const nextGame = upcomingGames.find((game: Game) => {
          const gameDate = toJsDate(game.eventDate as never);
          return gameDate ? gameDate > now : false;
        });
        if (nextGame) {
          setActiveCountdownGame(nextGame);
        }
        return;
      }

      setCountdown({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [activeCountdownGame, upcomingGames]);

  // Carousel for Games and Merchandise (unchanged)
  useEffect(() => {
    const gameInterval = setInterval(() => {
      setActiveGameIndex((prev) => (prev + 1) % (upcomingGames.length || 1));
    }, 8000);

    const merchInterval = setInterval(() => {
      setActiveNewsIndex(
        (prev) => (prev + 1) % (featuredMerchandise.length || 1)
      );
    }, 6000);

    const ticketInterval = setInterval(() => {
      setActiveNewsIndex(
        (prev) => (prev + 1) % (featuredTickets.length || 1)
      );
    }, 6000);

    return () => {
      clearInterval(gameInterval);
      clearInterval(merchInterval);
      clearInterval(ticketInterval);
    };
  }, [upcomingGames.length, featuredMerchandise.length, featuredTickets.length]);

  // Fetch Firebase Notifications for Popup Display
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        console.log('Fetching Firebase notifications for splash page...');
        // Cached (short TTL): active announcements shown in the popup. Dismissal +
        // date-validity filtering still happens per-render below.
        const notificationsData = await cachedRead(
          cacheKeys.custom('publicNotifications'),
          async () => {
            const notificationsQuery = query(
              collection(db, 'notifications'),
              where('active', '==', true),
              orderBy('createdAt', 'desc'),
              limit(5)
            );
            const snap = await getDocs(notificationsQuery);
            return snap.docs.map(d => ({ id: d.id, ...d.data() })) as Notification[];
          },
          CACHE_TTL.BUSINESS
        );

        // Filter out dismissed notifications and check date validity
        const validNotifications = notificationsData.filter(notification => {
          if (dismissedNotifications.includes(notification.id)) return false;
          
          const now = new Date();
          const startDate = toJsDate(notification.startDate);
          const endDate = toJsDate(notification.endDate);

          if (startDate && now < startDate) return false;
          if (endDate && now > endDate) return false;

          return true;
        });

        setFirebaseNotifications(validNotifications);
        console.log('Loaded Firebase notifications:', validNotifications.length);
        
        // Trigger alerts based on notification type and priority
        validNotifications.forEach(notification => {
          if (notification.type === 'announcement' && notification.priority === 'high') {
            setShowGameAlert(true);
          } else if (notification.type === 'promotion') {
            setShowMerchAlert(true);
          }
        });
        
      } catch (error) {
        console.error('Error fetching Firebase notifications:', error);
      }
    };

    fetchNotifications();
  }, [dismissedNotifications]);

  // Handle Click Outside for News Panel (unchanged)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        newsPanelRef.current &&
        !newsPanelRef.current.contains(event.target as Node)
      ) {
        setShowNewsPanel(false);
      }
    };

    if (showNewsPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNewsPanel]);

  // Handle video modal opening (unchanged)
  const handleVideoClick = () => {
    window.open(YOUTUBE_LIVE_STREAM, '_blank');
  };

  // Random Alerts and Popups (unchanged)
  useEffect(() => {
    const showRandomAlert = () => {
      const now = new Date();
      const alerts = [
        () => {
          const randomGameIndex = Math.floor(
            Math.random() * upcomingGames.length
          );
          const randomGame = upcomingGames[randomGameIndex];
          if (randomGame) {
            const gameDate = toJsDate(randomGame.eventDate);
            if (gameDate) {
              const daysUntilGame = Math.ceil(
                (gameDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
              );
              if (daysUntilGame < 14) {
                setActiveCountdownGame(randomGame);
                setShowGameAlert(true);
                setTimeout(() => setShowGameAlert(false), 10000);
              }
            }
          }
        },
        () => {
          const randomGameIndex = Math.floor(
            Math.random() * upcomingGames.length
          );
          const randomGame = upcomingGames[randomGameIndex];
          if (randomGame) {
            const gameDate = toJsDate(randomGame.eventDate);
            if (gameDate) {
              const daysUntilGame = Math.ceil(
                (gameDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
              );
              if (daysUntilGame < 7) {
                setActiveCountdownGame(randomGame);
                setShowTicketAlert(true);
                setTimeout(() => setShowTicketAlert(false), 10000);
              }
            }
          }
        },
      ];
      const randomAlert = alerts[Math.floor(Math.random() * alerts.length)];
      randomAlert();
    };

    const alertInterval = setInterval(() => {
      if (upcomingGames.length > 0 && featuredMerchandise.length > 0) {
        showRandomAlert();
      }
    }, 60000);

    return () => clearInterval(alertInterval);
  }, [upcomingGames, featuredMerchandise, activeCountdownGame]);

  // Control Body Overflow for Modals (unchanged)
  useEffect(() => {
    document.body.style.overflow =
      showNewsPanel || showVideoModal || showSeasonPopup || showMerchPopup
        ? 'hidden'
        : 'auto';
  }, [showNewsPanel, showVideoModal, showSeasonPopup, showMerchPopup]);

  // Scroll Handler (unchanged)
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 100);
  }, []);

  // Share Page Handler (unchanged)
  const sharePage = useCallback(
    async (
      title: string = 'Vipers MHL',
      text: string = 'Discover the latest from Vipers MHL!'
    ) => {
      if (navigator.share) {
        try {
          await navigator.share({ title, text, url: window.location.href });
          showNotification('Shared successfully!', 'success');
        } catch (err) {
          console.error('Share failed:', err);
          showNotification('Sharing failed', 'error');
        }
      } else {
        showNotification(
          'Share feature is not supported in your browser',
          'info'
        );
      }
    },
    []
  );

  // Load Instagram Widget (Elfsight) — deferred until section approaches viewport
  useEffect(() => {
    const target = document.querySelector('.instagram-feed-section');
    if (!target) return;

    let scriptEl: HTMLScriptElement | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          scriptEl = document.createElement('script');
          scriptEl.src = 'https://static.elfsight.com/platform/platform.js';
          scriptEl.async = true;
          document.body.appendChild(scriptEl);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
      if (scriptEl && document.body.contains(scriptEl)) {
        document.body.removeChild(scriptEl);
      }
    };
  }, []);

  // Scroll to Section (unchanged)
  const scrollToSection = useCallback((sectionId: string) => {
    const section = document.getElementById(sectionId);
    section?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Homepage shows only the Platinum family; the full list lives on /sponsor.
  const PLATINUM_TIERS = TIER_ORDER.slice(0, 4);
  const homeSponsors = featuredSponsors.filter(
    (s) => s.tier && PLATINUM_TIERS.includes(normalizeTier(s.tier))
  );

  // Accept a Timestamp | Date | string | undefined and guard internally so a
  // malformed/missing date renders as a fallback instead of throwing.
  const formatDate = (
    value: unknown,
    options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  ): string => {
    const d = toJsDate(value as never);
    return d ? d.toLocaleDateString('en-US', options) : 'TBD';
  };

  const formatTime = (value: unknown): string => {
    const d = toJsDate(value as never);
    return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  };

  // Format 24-hour time string (e.g., "19:30") to 12-hour format (e.g., "7:30 PM").
  // Guarded: a missing/malformed time must not throw and white-screen the home page.
  const formatGameTime = (time24?: string): string => {
    if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return 'TBD';
    const [hours, minutes] = time24.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 'TBD';
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const handleCountdownEnd = () => {
    if (upcomingGames.length > 0) {
      const currentIndex = upcomingGames.findIndex(game => game.id === activeCountdownGame?.id);
      const nextIndex = (currentIndex + 1) % upcomingGames.length;
      setActiveCountdownGame(upcomingGames[nextIndex]);
    }
  };

  // Add handler for showing details
  const handleShowDetails = (type: 'merchandise' | 'ticket', item: MerchandiseItem | Ticket) => {
    setShowDetails({
      type,
      item: {
        ...item,
        type
      } as (MerchandiseItem & { type: 'merchandise' }) | (Ticket & { type: 'ticket' })
    });
  };

  // Add handler for closing details
  const handleCloseDetails = () => {
    setShowDetails({ type: null, item: null });
  };

  // ── Display-only helpers (no data/state changes) ──────────────────────────
  // Full opponent name — never the last word only ("Boys").
  const opponentName = (game: Game): string =>
    (game.opponent || game.name || 'Opponent').trim();

  // Cards show the venue name only (first segment of the stored location);
  // the full string is kept for the tooltip.
  const shortVenue = (location?: string): string => {
    if (!location) return 'TBD';
    const first = location.split(':')[0].split(',')[0].trim();
    return first || location;
  };

  const dateParts = (value: unknown) => {
    const d = toJsDate(value as never);
    if (!d) return null;
    return {
      dow: d.toLocaleDateString('en-US', { weekday: 'short' }),
      day: d.getDate(),
      mon: d.toLocaleDateString('en-US', { month: 'short' }),
    };
  };

  const pad2 = (n: number): string => n.toString().padStart(2, '0');

  // Ticket product names carry the seat type as a prefix; the card title shows
  // the event part only (the seat type is shown as its own badge).
  const ticketTitle = (name: string): string =>
    name
      .replace(
        /^(GENERAL ADMISSION|WHISKEY STAR SKYBOX|CHILD ADMISSION|SKYBOX SEASON PASS|Viper Pit- Season Pass|VIP|SKY ?BOX)\s*[:\-–]\s*/i,
        ''
      )
      // The card already shows the date; drop a trailing "(Fri 11/13)".
      .replace(/\s*\((mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?\s+\d{1,2}\/\d{1,2}(\/\d{2,4})?\)\s*$/i, '')
      .trim() || name;

  // Seat-type color key shared with the Tickets page: GA = teal, VIP / Sky Box = gold.
  const seatBadgeClass = (seatType?: string, name?: string): string => {
    const t = `${seatType || ''} ${name || ''}`.toLowerCase();
    if (/sky ?box|whiskey|vip/.test(t)) return 'vb-badge--gold';
    if (/general|(^|[^a-z])ga([^a-z]|$)/.test(t)) return 'vb-badge--teal';
    return '';
  };

  const onCardKey = (e: React.KeyboardEvent, open: () => void) => {
    if (e.key === 'Enter') open();
  };

  const upcomingList = upcomingGames
    .filter((g) => g.id !== activeCountdownGame?.id)
    .slice(0, 5);

  const countdownUnits: { key: keyof typeof countdown; label: string }[] = [
    { key: 'days', label: 'Days' },
    { key: 'hours', label: 'Hours' },
    { key: 'minutes', label: 'Minutes' },
    { key: 'seconds', label: 'Seconds' },
  ];

  const vipersLogo = (className: string) => (
    <img src={vipersMain} alt="Breckenridge Vipers logo" className={className} />
  );

  return (
    <div
      className={`splash-page vb-page ${isLoaded ? 'loaded' : ''}`}
      data-theme="dark"
    >
      <NotificationBanner currentPage="home" />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="home-hero"
        ref={heroRef}
        id="hero"
        aria-labelledby="home-hero-title"
      >
        <div className="home-hero__media" aria-hidden="true">
          <img
            src={heroLockerRoom}
            alt=""
            fetchPriority="high"
            width={1920}
            height={1080}
          />
        </div>
        <div className="vb-container home-hero__inner">
          <img
            src={vipersMain}
            alt="Breckenridge Vipers logo"
            className="home-hero__logo"
          />
          <p className="vb-eyebrow">Mountain Hockey League &middot; Est. 2015</p>
          <h1 className="home-hero__title" id="home-hero-title">
            Breckenridge Vipers
          </h1>
          <p className="home-hero__lede">
            High-altitude hockey in Breckenridge, Colorado. Grab a seat at the
            Stephen C. West Ice Arena, rep the snake, and catch every game live.
          </p>
          <div className="home-hero__actions">
            <Link to="/tickets" className="vb-btn vb-btn--primary vb-btn--lg">
              <TicketIcon size={18} aria-hidden="true" /> Get Tickets
            </Link>
            <Link to="/merchandise" className="vb-btn vb-btn--ghost vb-btn--lg">
              <ShoppingBag size={18} aria-hidden="true" /> Shop Gear
            </Link>
            <a
              href="https://www.youtube.com/@mountainhockeyleague"
              target="_blank"
              rel="noopener noreferrer"
              className="vb-btn vb-btn--link home-hero__watch"
            >
              <PlayCircle size={18} aria-hidden="true" /> Watch Live
              <span className="home-sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </div>
      </section>

      <main className="home-main">
        {/* ── Next home game ─────────────────────────────────────────────── */}
        <section
          className="vb-section home-next"
          id="next-game"
          aria-labelledby="next-game-title"
        >
          <div className="vb-container">
            <header className="vb-section-header">
              <p className="vb-eyebrow">Next home game</p>
              <h2 className="vb-title" id="next-game-title">Puck Drop</h2>
            </header>

            {isLoadingGames ? (
              <div className="home-loading" role="status">Loading next game…</div>
            ) : errorGames ? (
              <div className="vb-empty" role="alert">
                <p className="vb-empty__title">We couldn&apos;t load the schedule</p>
                <p>{errorGames}</p>
              </div>
            ) : activeCountdownGame ? (
              <article className="home-matchup" aria-label="Featured matchup">
                <div className="home-matchup__top">
                  <span className={`vb-badge ${activeCountdownGame.isHome ? 'vb-badge--teal' : ''}`}>
                    {activeCountdownGame.isHome ? 'Home game' : 'Away game'}
                  </span>
                  {activeCountdownGame.promoText && (
                    <span className="vb-badge vb-badge--gold">{activeCountdownGame.promoText}</span>
                  )}
                </div>

                <div className="home-matchup__teams">
                  <div className="home-matchup__team">
                    <span className="home-matchup__logo">
                      {vipersLogo('home-matchup__logo-img')}
                    </span>
                    <span className="home-matchup__name">Breckenridge Vipers</span>
                  </div>
                  <span className="home-matchup__vs" aria-label={activeCountdownGame.isHome ? 'versus' : 'at'}>
                    {activeCountdownGame.isHome ? 'vs' : '@'}
                  </span>
                  <div className="home-matchup__team">
                    <span className="home-matchup__logo">
                      <LogoImg
                        src={activeCountdownGame.image}
                        alt={`${opponentName(activeCountdownGame)} logo`}
                        className="home-matchup__logo-img"
                        fallbackClassName="home-logo-fallback"
                        fallback={monogram(opponentName(activeCountdownGame))}
                      />
                    </span>
                    <span className="home-matchup__name">{opponentName(activeCountdownGame)}</span>
                  </div>
                </div>

                <ul className="home-matchup__meta">
                  <li>
                    <Calendar size={16} aria-hidden="true" />
                    {formatDate(activeCountdownGame.eventDate, { weekday: 'long', month: 'long', day: 'numeric' })}
                  </li>
                  <li>
                    <Clock size={16} aria-hidden="true" />
                    {formatGameTime(activeCountdownGame.eventTime)}
                  </li>
                  <li title={activeCountdownGame.eventLocation || undefined}>
                    <MapPin size={16} aria-hidden="true" />
                    {shortVenue(activeCountdownGame.eventLocation)}
                  </li>
                </ul>

                <div
                  className="home-countdown"
                  role="timer"
                  aria-label={`Puck drop in ${countdown.days} days, ${countdown.hours} hours, ${countdown.minutes} minutes`}
                >
                  {countdownUnits.map(({ key, label }) => (
                    <div key={key} className="home-countdown__tile" aria-hidden="true">
                      <span className="home-countdown__num">{pad2(countdown[key])}</span>
                      <span className="home-countdown__label">{label}</span>
                    </div>
                  ))}
                </div>

                <div className="home-matchup__actions">
                  <GetTicketsButton
                    game={activeCountdownGame}
                    className="vb-btn vb-btn--primary vb-btn--lg"
                    ariaLabel={`Get tickets for Vipers vs ${opponentName(activeCountdownGame)}`}
                  />
                  <Link to="/schedule" className="vb-btn vb-btn--secondary vb-btn--lg">
                    Full schedule
                  </Link>
                </div>
              </article>
            ) : (
              <div className="vb-empty">
                <p className="vb-empty__title">No upcoming home games</p>
                <p>The next schedule drop is coming soon — check back shortly.</p>
              </div>
            )}
          </div>
        </section>

        {/* ── Featured tickets ───────────────────────────────────────────── */}
        <section
          className="vb-section home-tickets"
          id="featured-tickets"
          aria-labelledby="featured-tickets-title"
        >
          <div className="vb-container">
            <div className="home-section-head">
              <header className="vb-section-header vb-section-header--left">
                <p className="vb-eyebrow">Tickets</p>
                <h2 className="vb-title" id="featured-tickets-title">Featured Tickets</h2>
              </header>
              <Link to="/tickets" className="vb-btn vb-btn--secondary vb-btn--sm home-section-head__action">
                View all tickets <ChevronRight size={16} aria-hidden="true" />
              </Link>
            </div>

            {isLoadingTickets ? (
              <div className="home-loading" role="status">Loading featured tickets…</div>
            ) : errorTickets ? (
              <div className="vb-empty" role="alert">
                <p className="vb-empty__title">We couldn&apos;t load tickets</p>
                <p>{errorTickets}</p>
              </div>
            ) : featuredTickets.length > 0 ? (
              <div className="vb-grid vb-grid--3">
                {featuredTickets.map((ticket) => {
                  const title = ticketTitle(ticket.name);
                  return (
                    <article
                      key={ticket.id}
                      className="vb-card vb-card--interactive home-ticket"
                      onClick={() => handleShowDetails('ticket', ticket)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => onCardKey(e, () => handleShowDetails('ticket', ticket))}
                      aria-label={`View details for ${ticket.name}`}
                    >
                      <div className="vb-card__body home-ticket__body">
                        <div className="home-ticket__head">
                          <LogoImg
                            src={(ticket.images || [])[0]}
                            alt={`${ticket.name} logo`}
                            className="home-ticket__logo"
                            fallbackClassName="home-logo-fallback"
                            fallback={monogram(title)}
                          />
                          <div className="home-ticket__heading">
                            <p className="home-ticket__date">
                              {formatDate(ticket.eventDate, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            <h3 className="vb-card__title">{title}</h3>
                          </div>
                        </div>

                        <ul className="home-meta">
                          <li>
                            <Clock size={15} aria-hidden="true" />
                            {ticket.eventTime ? formatGameTime(ticket.eventTime) : formatTime(ticket.eventDate)}
                          </li>
                          <li title={ticket.eventLocation || undefined}>
                            <MapPin size={15} aria-hidden="true" />
                            {ticket.eventLocation && ticket.eventLocation !== 'TBD'
                              ? shortVenue(ticket.eventLocation)
                              : 'Vipers Arena'}
                          </li>
                        </ul>

                        {ticket.seatType && (
                          <div className="home-ticket__tags">
                            <span className={`vb-badge ${seatBadgeClass(ticket.seatType, ticket.name)}`}>{ticket.seatType} seating</span>
                          </div>
                        )}

                        <div className="home-ticket__foot">
                          <p className="home-price">
                            <span className="home-price__label">From</span>
                            <strong>${ticket.price.toFixed(2)}</strong>
                          </p>
                          <Link
                            to="/tickets"
                            className="vb-btn vb-btn--primary vb-btn--sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Buy Tickets
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="vb-empty">
                <p className="vb-empty__title">No featured tickets right now</p>
                <p>
                  Browse every game and season pass on the{' '}
                  <Link to="/tickets" className="home-inline-link">tickets page</Link>.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Sponsors — only when there are real sponsors to show ────────── */}
        {homeSponsors.length > 0 && (
          <section
            className="vb-section vb-section--alt home-sponsors"
            aria-labelledby="sponsors-title"
          >
            <div className="vb-container">
              <header className="vb-section-header">
                <p className="vb-eyebrow">Proud partners</p>
                <h2 className="vb-title" id="sponsors-title">Our Sponsors</h2>
                <p className="vb-lede">
                  The businesses that power Breckenridge Vipers hockey — on the ice,
                  in the stands, and across the mountain.
                </p>
              </header>

              {/* Real sponsors from Firestore only, already ordered by tier. */}
              <SponsorCarousel
                items={homeSponsors}
                getKey={(s) => s.id}
                label="Our sponsors"
                renderItem={(sponsor, isClone) => {
                  const tierKey = sponsor.tier ? normalizeTier(sponsor.tier) : null;
                  const rank = tierKey ? tierRank(sponsor.tier) : 99;
                  // Platinum family + Gold read as "top tier" (gold badge).
                  const isTop = rank <= 4;
                  const showTier = tierKey && tierKey !== 'custom';
                  return (
                    <a
                      href={sponsor.website || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`home-sponsor ${isTop ? 'home-sponsor--top' : ''}`}
                      aria-label={`${sponsor.name} (opens in a new tab)`}
                      tabIndex={isClone ? -1 : undefined}
                    >
                      <span className="home-sponsor__logo">
                        <LogoImg
                          src={sponsor.logo}
                          alt={sponsor.name}
                          className="home-sponsor__img"
                          eager
                          fallbackClassName="home-sponsor__fallback"
                          fallback={sponsor.name}
                        />
                      </span>
                      <span className="home-sponsor__body">
                        <span className="home-sponsor__name">{sponsor.name}</span>
                        <span className="home-sponsor__row">
                          {showTier ? (
                            <span className={`vb-badge ${isTop ? 'vb-badge--gold' : ''}`}>
                              {tierLabel(sponsor.tier)}
                            </span>
                          ) : (
                            <span />
                          )}
                          <span className="home-sponsor__visit">
                            Visit <ExternalLink size={13} aria-hidden="true" />
                          </span>
                        </span>
                      </span>
                    </a>
                  );
                }}
              />

              <div className="home-section-foot home-section-foot--center">
                <Link to="/sponsor" className="vb-btn vb-btn--secondary">
                  Become a sponsor <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ── Upcoming games ─────────────────────────────────────────────── */}
        <section
          className="vb-section vb-section--alt home-games"
          id="upcoming-games"
          aria-labelledby="upcoming-games-title"
        >
          <div className="vb-container">
            <div className="home-section-head">
              <header className="vb-section-header vb-section-header--left">
                <p className="vb-eyebrow">Schedule</p>
                <h2 className="vb-title" id="upcoming-games-title">Upcoming Games</h2>
              </header>
              <Link to="/schedule" className="vb-btn vb-btn--secondary vb-btn--sm home-section-head__action">
                Full schedule <ChevronRight size={16} aria-hidden="true" />
              </Link>
            </div>

            {isLoadingGames ? (
              <div className="home-loading" role="status">Loading upcoming games…</div>
            ) : errorGames ? (
              <div className="vb-empty" role="alert">
                <p className="vb-empty__title">We couldn&apos;t load the schedule</p>
                <p>{errorGames}</p>
              </div>
            ) : upcomingList.length > 0 ? (
              <>
                <ol className="home-games__list">
                  {upcomingList.map((game: Game) => {
                    const parts = dateParts(game.eventDate);
                    const opp = opponentName(game);
                    return (
                      <li key={game.id}>
                        <article className="vb-game" aria-label={`Vipers ${game.isHome ? 'vs' : 'at'} ${opp}, ${formatDate(game.eventDate, { weekday: 'long', month: 'long', day: 'numeric' })}`}>
                          <div className="vb-game__date">
                            {parts ? (
                              <>
                                <span className="vb-game__dow">{parts.dow}</span>
                                <span className="vb-game__day">{parts.day}</span>
                                <span className="vb-game__mon">{parts.mon}</span>
                              </>
                            ) : (
                              <span className="vb-game__dow">TBD</span>
                            )}
                          </div>
                          <div className="vb-game__main">
                            <div className="vb-game__top">
                              <span className={`vb-badge ${game.isHome ? 'vb-badge--teal' : ''}`}>
                                {game.isHome ? 'Home' : 'Away'}
                              </span>
                            </div>
                            <div className="vb-game__matchup">
                              <span className="vb-game__team">
                                {vipersLogo('vb-game__logo')}
                                Vipers
                              </span>
                              <span className="vb-game__vs">{game.isHome ? 'vs' : '@'}</span>
                              <span className="vb-game__team">
                                <LogoImg
                                  src={game.image}
                                  alt={`${opp} logo`}
                                  className="vb-game__logo"
                                  fallbackClassName="home-logo-fallback"
                                  fallback={monogram(opp)}
                                />
                                <span className="home-team-name">{opp}</span>
                              </span>
                            </div>
                            <div className="vb-game__meta">
                              <span>
                                <Clock aria-hidden="true" /> {formatGameTime(game.eventTime)}
                              </span>
                              <span title={game.eventLocation || undefined}>
                                <MapPin aria-hidden="true" /> {shortVenue(game.eventLocation)}
                              </span>
                            </div>
                          </div>
                          <div className="vb-game__action">
                            <GameTicketStatus
                              game={game}
                              ariaLabel={`Get tickets for Vipers ${game.isHome ? 'vs' : 'at'} ${opp}`}
                            />
                          </div>
                        </article>
                      </li>
                    );
                  })}
                </ol>
                <div className="home-section-foot">
                  <Link to="/tickets" className="vb-btn vb-btn--link">
                    All tickets <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </div>
              </>
            ) : (
              <div className="vb-empty">
                <p className="vb-empty__title">No other games scheduled yet</p>
                <p>New dates are added as the season schedule is confirmed.</p>
              </div>
            )}
          </div>
        </section>

        {/* ── Featured merchandise ───────────────────────────────────────── */}
        <section
          className="vb-section home-merch"
          id="featured-merchandise"
          aria-labelledby="featured-merch-title"
        >
          <div className="vb-container">
            <div className="home-section-head">
              <header className="vb-section-header vb-section-header--left">
                <p className="vb-eyebrow">Team store</p>
                <h2 className="vb-title" id="featured-merch-title">Featured Merchandise</h2>
              </header>
              <Link to="/merchandise" className="vb-btn vb-btn--secondary vb-btn--sm home-section-head__action">
                Shop all gear <ChevronRight size={16} aria-hidden="true" />
              </Link>
            </div>

            {isLoadingMerch ? (
              <div className="home-loading" role="status">Loading featured merchandise…</div>
            ) : errorMerch ? (
              <div className="vb-empty" role="alert">
                <p className="vb-empty__title">We couldn&apos;t load merchandise</p>
                <p>{errorMerch}</p>
              </div>
            ) : featuredMerchandise.length > 0 ? (
              <div className="vb-grid vb-grid--4">
                {featuredMerchandise.map((item) => (
                  <article
                    key={item.id}
                    className="vb-card vb-card--interactive home-product"
                    onClick={() => handleShowDetails('merchandise', item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => onCardKey(e, () => handleShowDetails('merchandise', item))}
                    aria-label={`View details for ${item.name}`}
                  >
                    <div className="vb-card__media home-product__media">
                      <img
                        src={getProductImageUrl(item.image, item.name)}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                      />
                      {(item.sale || item.new || item.bestSeller) && (
                        <div className="home-product__badges">
                          {item.sale && <span className="vb-badge vb-badge--red home-badge-solid">Sale</span>}
                          {item.new && <span className="vb-badge vb-badge--teal home-badge-solid">New</span>}
                          {item.bestSeller && <span className="vb-badge vb-badge--gold home-badge-solid">Best seller</span>}
                        </div>
                      )}
                    </div>
                    <div className="vb-card__body home-product__body">
                      <h3 className="vb-card__title">{item.name}</h3>
                      <p className="home-price">
                        {item.sale && item.originalPrice ? (
                          <>
                            <strong className="home-price--sale">${item.price.toFixed(2)}</strong>
                            <s className="home-price__was">${item.originalPrice.toFixed(2)}</s>
                          </>
                        ) : (
                          <strong>${item.price.toFixed(2)}</strong>
                        )}
                      </p>
                      {((item.sizes && item.sizes.length > 0) || (item.colors && item.colors.length > 0)) && (
                        <div className="home-product__opts">
                          {item.sizes && item.sizes.length > 0 && (
                            <span className="home-product__sizes">
                              <span className="home-sr-only">Sizes: </span>
                              {item.sizes.join(' · ')}
                            </span>
                          )}
                          {item.colors && item.colors.length > 0 && (
                            <span className="home-product__colors" aria-label={`Colors: ${item.colors.join(', ')}`}>
                              {item.colors.slice(0, 6).map((color, index) => (
                                <span
                                  key={index}
                                  className="home-product__swatch"
                                  style={{ backgroundColor: color.toLowerCase() }}
                                  title={color}
                                />
                              ))}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="vb-empty">
                <p className="vb-empty__title">No featured gear right now</p>
                <p>
                  The full collection is always open in the{' '}
                  <Link to="/merchandise" className="home-inline-link">team store</Link>.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Latest news ────────────────────────────────────────────────── */}
        <section
          className="vb-section vb-section--alt home-news"
          id="latest-news"
          aria-labelledby="latest-news-title"
        >
          <div className="vb-container">
            <div className="home-section-head">
              <header className="vb-section-header vb-section-header--left">
                <p className="vb-eyebrow">Newsroom</p>
                <h2 className="vb-title" id="latest-news-title">Latest News</h2>
              </header>
              <button
                type="button"
                className="vb-btn vb-btn--secondary vb-btn--sm home-section-head__action"
                onClick={() => navigate('/news')}
              >
                All news <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>

            {isLoadingNews ? (
              <div className="home-loading" role="status">Loading latest news…</div>
            ) : errorNews ? (
              <div className="vb-empty" role="alert">
                <p className="vb-empty__title">We couldn&apos;t load the news</p>
                <p>{errorNews}</p>
              </div>
            ) : latestNews.length > 0 ? (
              <div className="vb-grid vb-grid--3">
                {latestNews.map((article) => (
                  <article key={article.id} className="vb-card vb-card--interactive home-article">
                    {(article.images?.length ?? 0) > 0 ? (
                      <div className="vb-card__media home-article__media">
                        <img
                          src={article.images?.[0]}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="vb-card__media home-article__media home-article__media--empty" aria-hidden="true">
                        <Megaphone size={28} />
                      </div>
                    )}
                    <div className="vb-card__body home-article__body">
                      <div className="home-article__meta">
                        <span className="vb-badge">
                          {(article.category || 'news').replace('-', ' ')}
                        </span>
                        <time className="vb-card__meta">
                          {formatDate(article.publishDate, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </time>
                      </div>
                      <h3 className="vb-card__title home-article__title">
                        <Link to={`/news/${article.slug}`} className="home-article__link">
                          {article.title}
                        </Link>
                      </h3>
                      {article.excerpt && <p className="home-article__excerpt">{article.excerpt}</p>}
                      <div className="home-article__foot">
                        {article.author && (
                          <span className="vb-card__meta">By {article.author}</span>
                        )}
                        <span className="home-article__more" aria-hidden="true">
                          Read more <ArrowRight size={15} />
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="vb-empty">
                <p className="vb-empty__title">No recent news</p>
                <p>Check back soon for the latest updates and announcements.</p>
              </div>
            )}
          </div>
        </section>

        {/* ── Instagram (Elfsight; the loader observes .instagram-feed-section) ── */}
        <section
          className="instagram-feed-section vb-section home-instagram"
          aria-labelledby="instagram-title"
        >
          <div className="vb-container">
            <div className="home-section-head">
              <header className="vb-section-header vb-section-header--left">
                <p className="vb-eyebrow">@vipersmhl</p>
                <h2 className="vb-title" id="instagram-title">Follow Us on Instagram</h2>
              </header>
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="vb-btn vb-btn--secondary vb-btn--sm home-section-head__action"
              >
                <Instagram size={16} aria-hidden="true" /> Follow
                <span className="home-sr-only"> (opens in a new tab)</span>
              </a>
            </div>
            <div className="instagram-feed-container">
              <div className="elfsight-app-7da015b0-4327-449c-b4f5-6d25cfdda4e5" data-elfsight-app-lazy></div>
            </div>
          </div>
        </section>
      </main>

      <div className="home-partner-slot">
        <div className="vb-container">
          <PartnerSlot surface="featuredSlot" label="Featured partners" />
        </div>
      </div>

      <Suspense fallback={
        <div className="footer-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading footer...</p>
          </div>
        </div>
      }>
        <Footer
          socialLinks={socialLinks}
          onShare={sharePage}
          navigate={navigate}
        />
      </Suspense>

      {/* ── Toast alerts (behavior unchanged; restyled as quiet toasts) ────── */}
      {showGameAlert && (() => {
        const gameNotification = firebaseNotifications.find(n =>
          n.type === 'announcement' && n.priority === 'high' &&
          !dismissedNotifications.includes(n.id)
        );

        if (!gameNotification && !activeCountdownGame) return null;

        return (
          <div
            className="alert-notification game-alert"
            role="alert"
            aria-label="Game Alert"
          >
            <div className="alert-content">
              <Calendar size={20} className="alert-icon" aria-hidden="true" />
              <div className="alert-text">
                <p className="alert-title">
                  {gameNotification ? gameNotification.title : 'Next Game Alert!'}
                </p>
                <p>
                  {gameNotification ? gameNotification.message :
                    activeCountdownGame ? `${opponentName(activeCountdownGame)} on ${formatDate(activeCountdownGame.eventDate)} at ${formatGameTime(activeCountdownGame.eventTime)}` : 'No upcoming game scheduled'
                  }
                </p>
                <Link
                  to={gameNotification?.buttonLink || '/tickets'}
                  className="vb-btn vb-btn--primary vb-btn--sm alert-action"
                >
                  {gameNotification?.buttonText || 'Get Tickets'}
                </Link>
              </div>
              <button
                className="alert-close"
                onClick={() => {
                  if (gameNotification) {
                    dismissNotification(gameNotification.id);
                  } else {
                    setShowGameAlert(false);
                  }
                }}
                aria-label="Close game alert"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        );
      })()}

      {showMerchAlert && (() => {
        const merchNotification = firebaseNotifications.find(n =>
          n.type === 'promotion' &&
          !dismissedNotifications.includes(n.id)
        );

        if (!merchNotification) return null;

        return (
          <div
            className="alert-notification merch-alert"
            role="alert"
            aria-label="Merchandise Alert"
          >
            <div className="alert-content">
              <ShoppingBag size={20} className="alert-icon" aria-hidden="true" />
              <div className="alert-text">
                <p className="alert-title">{merchNotification.title}</p>
                <p>{merchNotification.message}</p>
                <Link
                  to={merchNotification.buttonLink || '/merchandise'}
                  className="vb-btn vb-btn--primary vb-btn--sm alert-action"
                >
                  {merchNotification.buttonText || 'Shop Now'}
                </Link>
              </div>
              <button
                className="alert-close"
                onClick={() => dismissNotification(merchNotification.id)}
                aria-label="Close merch alert"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        );
      })()}

      {showTicketAlert && activeCountdownGame && (
        <div
          className="alert-notification ticket-alert"
          role="alert"
          aria-label="Ticket Alert"
        >
          <div className="alert-content">
            <TicketIcon size={20} className="alert-icon" aria-hidden="true" />
            <div className="alert-text">
              <p className="alert-title">Limited Tickets Alert!</p>
              <p>
                Tickets for {opponentName(activeCountdownGame)} are selling fast!
              </p>
              <Link to="/tickets" className="vb-btn vb-btn--primary vb-btn--sm alert-action">
                Secure Yours Now
              </Link>
            </div>
            <button
              className="alert-close"
              onClick={() => setShowTicketAlert(false)}
              aria-label="Close ticket alert"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {showSeasonPopup && (
        <div
          className="full-popup-overlay"
          onClick={() => setShowSeasonPopup(false)}
        >
          <div
            className="full-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Season Tickets Popup"
          >
            <button
              className="popup-close"
              onClick={() => setShowSeasonPopup(false)}
              aria-label="Close popup"
            >
              <X size={20} />
            </button>
            <div className="popup-content">
              <p className="vb-eyebrow">Season tickets</p>
              <h2>{CURRENT_SEASON_SHORT} Season Tickets Now Available!</h2>
              <p>
                Lock in your seats for every heart-pounding Vipers home game and
                save up to 25% compared to single game prices.
              </p>
              <ul className="popup-features">
                <li className="popup-feature">
                  <Check size={16} aria-hidden="true" /> Same great seats for all home games
                </li>
                <li className="popup-feature">
                  <Check size={16} aria-hidden="true" /> Exclusive season ticket holder events
                </li>
                <li className="popup-feature">
                  <Check size={16} aria-hidden="true" /> Priority access to playoff tickets
                </li>
                <li className="popup-feature">
                  <Check size={16} aria-hidden="true" /> 15% discount on team merchandise
                </li>
              </ul>
              <Link to="/tickets" className="vb-btn vb-btn--primary vb-btn--block popup-action">
                View Season Ticket Options
              </Link>
            </div>
          </div>
        </div>
      )}

      {showMerchPopup && (
        <div
          className="full-popup-overlay"
          onClick={() => setShowMerchPopup(false)}
        >
          <div
            className="full-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Merchandise Sale Popup"
          >
            <button
              className="popup-close"
              onClick={() => setShowMerchPopup(false)}
              aria-label="Close popup"
            >
              <X size={20} />
            </button>
            <div className="popup-content">
              <p className="vb-eyebrow">Team store</p>
              <h2>Limited Time Offer!</h2>
              <p>Get 25% off all Vipers jerseys and apparel this week only!</p>
              <div className="popup-image">
                <img src="/assets/jersey-sale.jpg" alt="Vipers Jersey" />
              </div>
              <Link to="/merchandise" className="vb-btn vb-btn--primary vb-btn--block popup-action">
                Shop The Sale
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── Details modal ─────────────────────────────────────────────────── */}
      {showDetails.type && showDetails.item && (
        <div className="details-modal-overlay" onClick={handleCloseDetails}>
          <div
            className="details-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-detail-title"
          >
            <button className="home-modal__close" onClick={handleCloseDetails} aria-label="Close details">
              <X size={20} />
            </button>
            {showDetails.type === 'merchandise' && showDetails.item.type === 'merchandise' ? (
              <div className="home-detail">
                <div className="home-detail__media">
                  <img src={getProductImageUrl(showDetails.item.image, showDetails.item.name)} alt={showDetails.item.name} />
                </div>
                <p className="vb-eyebrow">Team store</p>
                <h2 className="home-detail__title" id="home-detail-title">{showDetails.item.name}</h2>
                <p className="home-price home-price--lg">
                  {showDetails.item.sale && showDetails.item.originalPrice ? (
                    <>
                      <strong className="home-price--sale">${showDetails.item.price.toFixed(2)}</strong>
                      <s className="home-price__was">${showDetails.item.originalPrice.toFixed(2)}</s>
                    </>
                  ) : (
                    <strong>${showDetails.item.price.toFixed(2)}</strong>
                  )}
                </p>
                {showDetails.item.description && (
                  <p className="home-detail__desc">{showDetails.item.description}</p>
                )}
                {(showDetails.item.details?.length ?? 0) > 0 && (
                  <ul className="home-detail__list">
                    {showDetails.item.details?.map((detail: string, index: number) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
                <div className="home-detail__actions">
                  <Link to="/merchandise" className="vb-btn vb-btn--primary" onClick={handleCloseDetails}>
                    Shop in the team store
                  </Link>
                </div>
              </div>
            ) : showDetails.type === 'ticket' && showDetails.item.type === 'ticket' ? (
              <div className="home-detail">
                {(showDetails.item.images || [])[0] && (
                  <div className="home-detail__media home-detail__media--logo">
                    <img src={(showDetails.item.images || [])[0]} alt={showDetails.item.name} />
                  </div>
                )}
                <p className="vb-eyebrow">Tickets</p>
                <h2 className="home-detail__title" id="home-detail-title">{showDetails.item.name}</h2>
                <p className="home-price home-price--lg">
                  {showDetails.item.isOnSale && showDetails.item.originalPrice ? (
                    <>
                      <strong className="home-price--sale">${showDetails.item.price.toFixed(2)}</strong>
                      <s className="home-price__was">${showDetails.item.originalPrice.toFixed(2)}</s>
                    </>
                  ) : (
                    <strong>${showDetails.item.price.toFixed(2)}</strong>
                  )}
                </p>
                <ul className="home-detail__facts">
                  <li><Calendar size={16} aria-hidden="true" /> {formatDate(showDetails.item.eventDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</li>
                  <li><Clock size={16} aria-hidden="true" /> {showDetails.item.eventTime ? formatGameTime(showDetails.item.eventTime) : formatTime(showDetails.item.eventDate)}</li>
                  <li title={showDetails.item.eventLocation || undefined}><MapPin size={16} aria-hidden="true" /> {shortVenue(showDetails.item.eventLocation)}</li>
                </ul>
                {showDetails.item.description && (
                  <p className="home-detail__desc">{showDetails.item.description}</p>
                )}
                <dl className="home-detail__info">
                  {showDetails.item.ticketType && (
                    <div><dt>Type</dt><dd>{showDetails.item.ticketType}</dd></div>
                  )}
                  {showDetails.item.seatType && (
                    <div><dt>Seating</dt><dd>{showDetails.item.seatType}</dd></div>
                  )}
                  <div>
                    <dt>Availability</dt>
                    <dd>
                      {showDetails.item.stock?.available && showDetails.item.stock.available > 0 ? (
                        <span className="vb-badge vb-badge--teal">{showDetails.item.stock.available} available</span>
                      ) : (
                        <span className="vb-badge vb-badge--red">Sold out</span>
                      )}
                    </dd>
                  </div>
                </dl>
                <div className="home-detail__actions">
                  {showDetails.item.stock?.available && showDetails.item.stock.available > 0 ? (
                    <Link
                      to={`/tickets?event=${showDetails.item.id}`}
                      className="vb-btn vb-btn--primary"
                      onClick={handleCloseDetails}
                    >
                      Buy Tickets
                    </Link>
                  ) : null}
                  <Link
                    to="/tickets"
                    className="vb-btn vb-btn--secondary"
                    onClick={handleCloseDetails}
                  >
                    View All Tickets
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <button
        className={`back-to-top ${isScrolled ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        tabIndex={isScrolled ? 0 : -1}
      >
        <ArrowUp size={20} />
      </button>
    </div>
  );
};

export default SplashPage;
